import React from 'react'
import { Link } from 'react-router-dom'
import {
  COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  Icon,
  reducers,
} from '@adc/ui-morpheus'
import { TRANSACTIONS } from '../../constants/tableTypes'

const SEARCH_ANALYZER = 'searchAnalyzer'
const SORT_ANALYZER = 'sortAnalyzer'
const getDefaultProps = width => ({
  width,
  isResizable: true,
  isReorderable: true,
  isVisible: false,
})

export const decomposeUrn = urn => {
  if (!urn) {
    return null
  }

  const parts = urn.split(':')
  if (parts.length !== 3) {
    return null
  }

  const [namespace, resourceType, resourceId] = parts
  return {
    namespace,
    resourceType,
    resourceId,
  }
}

/**
 * Extract a specific URN type ID from the associated_urns array
 */
const extractUrnId = (urns, urnType) => {
  if (!Array.isArray(urns)) return null
  const match = urns.find(u => u.urn_type === urnType)
  if (!match) return null
  return decomposeUrn(match.associated_urn)?.resourceId || null
}

/**
 * Builds the detail page link from row data
 */
const getDetailLink = rowData => {
  const txResourceId = decomposeUrn(rowData?.transaction_urn)?.resourceId
  const propertyId =
    rowData?.property?.property_id ||
    extractUrnId(rowData?.associated_urns, 'PROPERTY')

  if (propertyId && txResourceId) {
    return `/transactions/property/${propertyId}/transaction/${txResourceId}`
  }
  // Fallback: resolver route handles property resolution
  if (txResourceId) {
    return `/transactions/${txResourceId}`
  }
  return null
}

export const tableConfig = {
  config: {
    entityKey: TRANSACTIONS,
    primaryKeyField: 'transaction_urn',
    allowExportCSV: true,
  },
  fieldDefinitions: [
    {
      name: 'Selected',
      apiRef: 'selected',
      columnDefinition: {
        type: COLUMN_TYPES.CHECKBOX,
        colProps: { width: 40, fixed: true },
      },
    },
    // --- Primary identifiers (first columns) ---
    {
      name: 'Transaction URN',
      apiRef: 'transaction_urn',
      sortAnalyzer: '',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...getDefaultProps(240),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.SORTED,
          sortAscending: false,
          searchable: true,
        },
      },
    },
    {
      name: 'Property ID',
      apiRef: 'property.property_id',
      searchAnalyzer: '',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(100),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Listing ID',
      apiRef: 'listing.listing_id',
      searchAnalyzer: '',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(100),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Lead Intake ID',
      apiRef: 'associated_urns',
      formatOptions: { custom: 'leadIntakeFormatter' },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(110),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTABLE,
          searchable: false,
        },
      },
    },
    // --- Key data columns ---
    {
      name: 'Status',
      apiRef: 'listing.listing_status',
      searchAnalyzer: '',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(160),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Product',
      apiRef: 'listing.listing_configuration.product_type',
      searchAnalyzer: '',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(120),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Address',
      apiRef: 'property.property_name',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...getDefaultProps(300),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'City',
      apiRef: 'property.summary.address.municipality',
      searchAnalyzer: '',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...getDefaultProps(120),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'State',
      apiRef: 'property.summary.address.country_primary_subdivision',
      searchAnalyzer: '',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(60),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Auction Result',
      apiRef: 'selling.online_segment.result.auction_status',
      searchAnalyzer: '',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(120),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Bids',
      apiRef: 'selling.online_segment.bid_count',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(60),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'High Bid',
      apiRef: 'selling.online_segment.current_highest_bid.bid_amount',
      columnDefinition: {
        type: COLUMN_TYPES.CURRENCY,
        colProps: {
          ...getDefaultProps(110),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'Created',
      apiRef: 'created_date',
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...getDefaultProps(120),
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    // --- Hidden columns (toggle via column picker) ---
    {
      name: 'Listing Code',
      apiRef: 'listing.listing_code',
      searchAnalyzer: '',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(100),
          isVisible: false,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Reserve',
      apiRef: 'selling.online_segment.current_reserve_amount',
      columnDefinition: {
        type: COLUMN_TYPES.CURRENCY,
        colProps: {
          ...getDefaultProps(110),
          isVisible: false,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'Starting Bid',
      apiRef: 'selling.online_segment.starting_bid_amount',
      columnDefinition: {
        type: COLUMN_TYPES.CURRENCY,
        colProps: {
          ...getDefaultProps(110),
          isVisible: false,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'Reserve Status',
      apiRef: 'selling.online_segment.reserve_status',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(120),
          isVisible: false,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'Organization',
      apiRef: 'associated_urns',
      formatOptions: { custom: 'organizationFormatter' },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...getDefaultProps(120),
          isVisible: false,
          sort: COLUMN_SORT_SETTING.NOT_SORTABLE,
        },
      },
    },
    // View action column
    {
      name: 'View',
      apiRef: '__actions__',
      columnDefinition: {
        type: COLUMN_TYPES.ACTION,
        actions: ['Link'],
        colProps: { width: 50, fixedRight: true, align: 'center' },
      },
    },
  ],
  filterClauses: [],
  sortBy: [
    {
      field: 'created_date',
      order: 'desc',
    },
  ],
}

const linkColumnAction =
  () => (actionIndex, rowIndex, primaryId, data, onRowAction) => {
    const link = getDetailLink(data)
    if (!link) return null
    return (
      <Link
        style={{ margin: -8, padding: 8 }}
        key={actionIndex}
        to={link}
        data-elm-id={`action_view_${rowIndex}`}
      >
        <Icon
          size='large'
          style={{ marginTop: -3 }}
          name='eye'
          color='info'
          onClick={() => onRowAction(rowIndex)}
        />
      </Link>
    )
  }

/**
 * Column selector for backwards compatibility with DataTableWrapper
 */
export const columnSelector = state =>
  state.fieldDefinitions
    .filter(field => field.columnDefinition)
    .map(col => {
      const column = {
        ...col.columnDefinition,
        ...{ field: col.apiRef, title: col.name },
      }
      if (column.actions) column.actions = column.actions.map(linkColumnAction)
      if (col.searchType) column.searchType = col.searchType
      if (col.options) column.options = col.options
      if (col.formatOptions) column.formatOptions = col.formatOptions
      if (SEARCH_ANALYZER in col) column.searchAnalyzer = col.searchAnalyzer
      if (SORT_ANALYZER in col) column.sortAnalyzer = col.sortAnalyzer
      return column
    })

export const getFilterClauses = state => state.filterClauses

export default reducers.dataTable.dataTable(TRANSACTIONS, tableConfig)
